import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, LOGO_URL, imgUrl } from "../lib/api";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Eye,
  Trash2,
  Plus,
  Edit3,
  CircleDollarSign,
  Inbox,
  Package,
} from "lucide-react";

const emptyProduct = {
  name: "",
  slug: "",
  short_description: "",
  description: "",
  price: 0,
  currency: "usd",
  category: "",
  materials: "",
  images: [],
  in_stock: true,
  featured: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [me, setMe] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null
  const [showForm, setShowForm] = useState(false);

  const logout = () => {
    localStorage.removeItem("wbz_admin_token");
    localStorage.removeItem("wbz_admin_email");
    navigate("/admin/login");
  };

  const loadAll = async () => {
    try {
      const [inq, prod, tx] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/products"),
        api.get("/admin/transactions"),
      ]);
      setInquiries(inq.data || []);
      setProducts(prod.data || []);
      setTransactions(tx.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/admin/login");
      }
    }
  };

  useEffect(() => {
    api
      .get("/admin/me")
      .then((r) => setMe(r.data))
      .catch(() => navigate("/admin/login"));
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => {
    await api.put(`/admin/inquiries/${id}/read`);
    loadAll();
  };
  const deleteInquiry = async (id) => {
    if (!confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    loadAll();
  };
  const deleteProduct = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      price: Number(editing.price),
      images:
        typeof editing.images === "string"
          ? editing.images.split("\n").map((s) => s.trim()).filter(Boolean)
          : editing.images,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      setEditing(null);
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const startEdit = (p) => {
    setEditing({
      ...p,
      images: (p.images || []).join("\n"),
    });
    setShowForm(true);
  };

  const startNew = () => {
    setEditing({ ...emptyProduct, images: "" });
    setShowForm(true);
  };

  const unread = inquiries.filter((i) => !i.is_read).length;
  const totalRevenue = transactions
    .filter((t) => t.payment_status === "paid")
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div data-testid="admin-page" className="bg-stone-950 text-stone-50 min-h-screen pt-12 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-stone-800 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="" className="h-12 w-12 logo-invert" />
            <div>
              <p className="text-overline text-amber-600">Admin</p>
              <h1 className="font-display text-3xl font-bold">Workshop dashboard</h1>
              {me && <p className="text-stone-500 text-sm mt-1">{me.email}</p>}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="border border-stone-800 hover:border-amber-600 text-stone-300 hover:text-amber-500 px-5 py-3 text-overline flex items-center gap-2"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <Stat icon={Inbox} label="New inquiries" value={unread} />
          <Stat icon={Package} label="Products" value={products.length} />
          <Stat
            icon={CircleDollarSign}
            label="Revenue (paid)"
            value={`$${totalRevenue.toFixed(2)}`}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-3 border-b border-stone-800 mb-10">
          {[
            ["inquiries", `Inquiries (${inquiries.length})`],
            ["products", `Products (${products.length})`],
            ["orders", `Orders (${transactions.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`text-overline px-5 py-3 border-b-2 transition-colors ${
                tab === key
                  ? "border-amber-600 text-amber-500"
                  : "border-transparent text-stone-400 hover:text-stone-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "inquiries" && (
          <div data-testid="admin-inquiries">
            {inquiries.length === 0 ? (
              <p className="text-stone-500">No inquiries yet.</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div
                    key={inq.id}
                    className={`border p-6 ${
                      inq.is_read
                        ? "border-stone-800 bg-stone-900/20"
                        : "border-amber-700/60 bg-stone-900/50"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          {!inq.is_read && (
                            <span className="text-overline bg-amber-700 text-stone-50 px-2 py-1">
                              NEW
                            </span>
                          )}
                          <p className="text-stone-50 font-semibold">
                            {inq.name || inq.email}
                          </p>
                          <span className="text-stone-500 text-xs">
                            {new Date(inq.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-stone-400 text-sm">
                          <a
                            href={`mailto:${inq.email}`}
                            className="text-amber-500 hover:text-amber-400"
                          >
                            {inq.email}
                          </a>
                          {inq.project_type && ` · ${inq.project_type}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`mailto:${inq.email}?subject=Re: your custom build inquiry`}
                          className="border border-stone-700 hover:border-amber-500 text-stone-300 hover:text-amber-500 p-2"
                          title="Reply"
                        >
                          <Mail size={16} />
                        </a>
                        {!inq.is_read && (
                          <button
                            onClick={() => markRead(inq.id)}
                            className="border border-stone-700 hover:border-amber-500 text-stone-300 hover:text-amber-500 p-2"
                            title="Mark read"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => deleteInquiry(inq.id)}
                          data-testid={`delete-inquiry-${inq.id}`}
                          className="border border-stone-700 hover:border-red-500 text-stone-300 hover:text-red-500 p-2"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-stone-300 mt-4 whitespace-pre-wrap leading-relaxed">
                      {inq.message}
                    </p>
                    {inq.email_sent === false && (
                      <p className="mt-3 text-xs text-amber-600">
                        Note: email forwarding to inbox is not yet enabled (Resend
                        API key missing). The inquiry is saved here but no email
                        was sent.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-6">
              <p className="text-stone-400 text-sm">
                Manage everything for sale on the shop.
              </p>
              <button
                onClick={startNew}
                data-testid="admin-add-product-btn"
                className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-5 py-3 text-overline flex items-center gap-2"
              >
                <Plus size={14} /> New product
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <div
                  key={p.id}
                  className="border border-stone-800 bg-stone-900/40"
                >
                  <div className="aspect-[4/3] bg-stone-950 overflow-hidden">
                    {p.images?.[0] && (
                      <img
                        src={imgUrl(p.images[0])}
                        alt={p.name}
                        className="w-full h-full object-contain p-2"
                      />
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-overline text-amber-600 mb-2">
                      {p.category}
                    </p>
                    <h3 className="font-display font-bold text-lg">{p.name}</h3>
                    <p className="font-display text-xl mt-2">${p.price}</p>
                    <p className="text-xs text-stone-500 mt-2">
                      {p.in_stock ? "In stock" : "Sold"}
                      {p.featured && " · Featured"}
                    </p>
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => startEdit(p)}
                        data-testid={`edit-product-${p.slug}`}
                        className="flex-1 border border-stone-800 hover:border-amber-500 text-stone-300 hover:text-amber-500 p-2 text-overline flex items-center justify-center gap-2"
                      >
                        <Edit3 size={14} /> Edit
                      </button>
                      <button
                        onClick={() => deleteProduct(p.id)}
                        data-testid={`delete-product-${p.slug}`}
                        className="border border-stone-800 hover:border-red-500 text-stone-300 hover:text-red-500 p-2"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders">
            {transactions.length === 0 ? (
              <p className="text-stone-500">No transactions yet.</p>
            ) : (
              <div className="overflow-x-auto border border-stone-800">
                <table className="w-full text-sm">
                  <thead className="bg-stone-900">
                    <tr>
                      <th className="text-left p-4 text-overline text-stone-400">Date</th>
                      <th className="text-left p-4 text-overline text-stone-400">Product</th>
                      <th className="text-left p-4 text-overline text-stone-400">Amount</th>
                      <th className="text-left p-4 text-overline text-stone-400">Status</th>
                      <th className="text-left p-4 text-overline text-stone-400">Session</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.session_id} className="border-t border-stone-800">
                        <td className="p-4 text-stone-400">
                          {new Date(t.created_at).toLocaleString()}
                        </td>
                        <td className="p-4 text-stone-100">{t.product_name}</td>
                        <td className="p-4 text-stone-100">
                          ${t.amount?.toFixed(2)} {t.currency?.toUpperCase()}
                        </td>
                        <td className="p-4">
                          <span
                            className={`text-overline px-3 py-1 ${
                              t.payment_status === "paid"
                                ? "bg-amber-700 text-stone-50"
                                : t.payment_status === "pending"
                                ? "bg-stone-800 text-stone-300"
                                : "bg-red-900/40 text-red-400"
                            }`}
                          >
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="p-4 text-stone-500 text-xs font-mono">
                          {t.session_id?.slice(0, 24)}…
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && editing && (
        <div className="fixed inset-0 bg-stone-950/90 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-6">
          <form
            onSubmit={saveProduct}
            data-testid="admin-product-form"
            className="bg-stone-900 border border-stone-700 max-w-2xl w-full p-8 my-12 space-y-5"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-display text-2xl font-bold">
                {editing.id ? "Edit product" : "New product"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="text-stone-400 hover:text-stone-50"
              >
                ✕
              </button>
            </div>
            <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field
              label="Slug (url-friendly)"
              value={editing.slug}
              onChange={(v) => setEditing({ ...editing, slug: v })}
            />
            <Field
              label="Category"
              value={editing.category}
              onChange={(v) => setEditing({ ...editing, category: v })}
            />
            <Field
              label="Materials"
              value={editing.materials}
              onChange={(v) => setEditing({ ...editing, materials: v })}
            />
            <Field
              label="Short description"
              value={editing.short_description}
              onChange={(v) => setEditing({ ...editing, short_description: v })}
            />
            <div>
              <label className="text-overline text-stone-500 block mb-2">
                Description
              </label>
              <textarea
                rows={4}
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none p-4 text-stone-100"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Price (USD)"
                type="number"
                value={editing.price}
                onChange={(v) => setEditing({ ...editing, price: v })}
              />
              <div>
                <label className="text-overline text-stone-500 block mb-2">
                  Currency
                </label>
                <input
                  type="text"
                  value={editing.currency}
                  onChange={(e) => setEditing({ ...editing, currency: e.target.value })}
                  className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none p-4 text-stone-100"
                />
              </div>
            </div>
            <div>
              <label className="text-overline text-stone-500 block mb-2">
                Image URLs (one per line)
              </label>
              <textarea
                rows={4}
                value={editing.images}
                onChange={(e) => setEditing({ ...editing, images: e.target.value })}
                className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none p-4 text-stone-100 font-mono text-xs"
              />
            </div>
            <div className="flex gap-6 pt-2">
              <label className="flex items-center gap-2 text-stone-300 text-sm">
                <input
                  type="checkbox"
                  checked={editing.in_stock}
                  onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })}
                />
                In stock
              </label>
              <label className="flex items-center gap-2 text-stone-300 text-sm">
                <input
                  type="checkbox"
                  checked={editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                />
                Featured
              </label>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                data-testid="admin-product-save"
                className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-6 py-4 text-overline flex-1"
              >
                {editing.id ? "Save changes" : "Add product"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="border border-stone-700 hover:border-stone-500 px-6 py-4 text-overline text-stone-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-stone-800 bg-stone-900/40 p-6 flex items-center gap-5">
      <div className="w-12 h-12 border border-stone-800 flex items-center justify-center text-amber-600">
        <Icon size={20} />
      </div>
      <div>
        <p className="text-overline text-stone-500">{label}</p>
        <p className="font-display text-3xl font-bold mt-1">{value}</p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-overline text-stone-500 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none p-4 text-stone-100"
      />
    </div>
  );
}
